import { PartSearchResultDetailsPage } from '@/entities/PartSearchResultDetailsPage';
import { ExactPartSearchResult } from '@/entities/ExactPartSearchResult';
import URI from 'urijs';
import { isNullOrWhitespace } from '@/helper/isNullOrWhitespace';
import { apiClient } from './api-client';
import { PartSearchResultDetails } from '@/entities/PartSearchResultDetails';
import {QuickSearchResult} from '@/entities/quick-search/QuickSearchResult';
import { VinSearchResult } from '@/entities/vin-search/VinSearchResult';

/**
 * Service to search for parts.
 */
export class PartSearchService {
    private get searchLink(): URI {
        return new URI('/api/search');
    }

    /**
     * Search for the given term.
     * @param searchTerm term that is searched for
     * @param brand an optional filter for a specific brand
     * @param category an optional filter for a specific category
     * @param pageNumber the page that should be fetched
     * @param numberOfResultsPerPage the number of results that should be fetched per page
     */
    public async getSearchResults(
        searchTerm: string,
        brand: string,
        category: string,
        pageNumber: number,
        numberOfResultsPerPage: number
    ): Promise<PartSearchResultDetailsPage> {
        const searchParams = {
            searchTerm,
            brand: isNullOrWhitespace(brand) ? undefined : brand,
            category: isNullOrWhitespace(category) ? undefined : category,
            pageNumber: pageNumber < 1 ? 1 : pageNumber,
            numberOfResultsPerPage: numberOfResultsPerPage < 1 ? 12 : numberOfResultsPerPage,
        };

        const res = await apiClient.get<PartSearchResultDetailsPage>(this.searchLink.addQuery(searchParams).toString());

        return res.data;
    }

    /**
     * Search for a small set of parts with the given search term.
     * @param searchTerm search term that should be used to find parts
     */
    public async getQuickSearchResults(searchTerm: string): Promise<QuickSearchResult> {
        const res = await apiClient.get<QuickSearchResult>(
            this.searchLink
                .segmentCoded('quick-search')
                .addQuery({
                    searchTerm,
                    numberOfResultsPerPage: 10,
                })
                .toString()
        );

        return res.data;
    }

    /**
     * Search for specific VIN.
     * @param searchTerm a specific VIN
     */
     public async getVinSearchResult(searchTerm: string): Promise<VinSearchResult> {
        const res = await apiClient.get<VinSearchResult>(
            this.searchLink
                .segmentCoded('vin-search')
                .addQuery({
                    searchTerm,
                })
                .toString()
        );

        return res.data;
    }

    /**
     * Search for exact matches of the given part number.
     * @param partNumber is used for the search
     */
    public async searchPartsByPartNumber(partNumber: string): Promise<ExactPartSearchResult> {
        const res = await apiClient.get<ExactPartSearchResult>(
            this.searchLink
                .segmentCoded('exact')
                .segmentCoded(partNumber)
                .toString()
        );

        return res.data;
    }

    /**
     * Search for parts by given combinations of part numbers and brand.
     * @param partNumbers part numbers
     * @param brand brand code
     */
    public async searchByPartNumbersAndBrand(partNumbers: string[], brand: string): Promise<PartSearchResultDetails[]> {
        const res = await apiClient.post(this.searchLink.segmentCoded('part-number-brand').toString(), {
            partNumbers,
            brand,
        });

        return res.data;
    }

    /**
     * Search for parts and get a shuffled list of parts
     * @param searchTerm term that is searched for
     * @param brand an optional filter for a specific brand
     * @param category an optional filter for a specific category
     * @param numberOfResults the number of results that should be fetched per page
     */
    public async getSearchResultsShuffled(
        searchTerm: string,
        brand: string,
        category: string,
        numberOfResults: number
    ): Promise<PartSearchResultDetailsPage> {
        const searchParams = {
            searchTerm,
            brand: isNullOrWhitespace(brand) ? undefined : brand,
            category: isNullOrWhitespace(category) ? undefined : category,
            numberOfResults: numberOfResults < 1 ? 0 : numberOfResults,
        };

        const res = await apiClient.get<PartSearchResultDetailsPage>(
            this.searchLink
                .segment('parts-shuffled')
                .addQuery(searchParams)
                .toString()
        );

        return res.data;
    }

    public getUrlForPartSearchPage(searchTerm: string, brand: string, pageNumber: number, category: string): string {
        return `/PartSearch?searchTerm=${searchTerm}&brand=${brand}&pageNumber=${pageNumber}&category=${category}`;
    }
}
